/**
 * Public plans hook — feeds the marketing ``/pricing-plans`` page
 * from ``GET /public/plans``. No auth required.
 *
 * The response carries everything the public pricing page needs:
 * tier cards (name, description, prices, recommended-flag, default
 * entitlements), entitlement-key catalog metadata for the bullets +
 * detail compare table, and admin-managed numeric metric rows. No
 * hardcoded tier→features mapping in the FE.
 */

import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { apiFetch } from "@/lib/api";

const API = "/api/v1";


export interface PublicPlanCard {
  tier: string;
  name: string;
  description: string;
  /** English overrides (null ⇒ fall back to ``name`` / ``description``). */
  name_en: string | null;
  description_en: string | null;
  /** Překlady per jazyk platformy ``{kód jazyka: text}`` (KAN-1804); ``*_en`` je zrcadlo. */
  name_i18n?: Record<string, string>;
  description_i18n?: Record<string, string>;
  price_monthly_cents: number | null;
  price_annual_cents: number | null;
  currency: string;
  /** Trial length in days; null/0 = the tier has no trial (KAN-1806). */
  trial_days?: number | null;
  default_entitlements: string[];
  /** Take-rate on orders, in basis points (250 = 2,5 %). */
  platform_fee_bps: number;
  /**
   * Monthly ceiling on that take-rate in ``platform_fee_cap_currency``
   * minor units; ``null`` = uncapped. Its own currency, not the
   * subscription ``currency`` — the ceiling is denominated in the
   * platform base currency.
   */
  platform_fee_cap_monthly_minor: number | null;
  platform_fee_cap_currency: string;
  sort_order: number;
  is_recommended: boolean;
}

export interface PublicEntitlementMeta {
  key: string;
  display_name: string;
  description: string;
  description_marketing: string;
  /** English overrides (null ⇒ fall back to the base text by language). */
  display_name_en: string | null;
  description_en: string | null;
  description_marketing_en: string | null;
  /** Překlady per jazyk platformy ``{kód jazyka: text}`` (KAN-1804); ``*_en`` je zrcadlo. */
  display_name_i18n?: Record<string, string>;
  description_i18n?: Record<string, string>;
  description_marketing_i18n?: Record<string, string>;
  group: string | null;
  is_wired: boolean;
  is_public_listing: boolean;
  is_card_highlight: boolean;
}

export type PlanMetricDirection = "ascends" | "descends" | "none";

export interface PublicPlanMetric {
  key: string;
  display_name: string;
  description: string;
  group: string | null;
  /** Fixed suffix after the number (e.g. "GB"). Empty when `unit_key` is set. */
  unit: string;
  /**
   * i18n key rendering the number and its unit together (`"{{count}} dní"`).
   * Set when the unit is a countable noun whose form depends on the number —
   * a fixed suffix reads as "4 dní" where Czech wants "4 dny". Empty ⇒ `unit`.
   */
  unit_key: string;
  sort_order: number;
  unlimited_label_key: string;
  /**
   * Which way the row reads as the tier gets dearer. `ascends` — more is more
   * generous (every allowance); `descends` — a rate the tenant pays; `none` —
   * not comparable, or nobody has said. Read by the deployed smoke, which
   * cannot compare two finite values without it (KAN-1062); the pricing page
   * itself does not render it.
   */
  direction: PlanMetricDirection;
  values: Record<string, number | null>;
}

export interface PublicPlansListResponse {
  items: PublicPlanCard[];
  entitlements: PublicEntitlementMeta[];
  metrics: PublicPlanMetric[];
  /** Is the platform operator a VAT payer (company profile)? KAN-1806. */
  vat_payer?: boolean;
}

export function usePublicPlans(
  options?: Omit<
    UseQueryOptions<PublicPlansListResponse>,
    "queryKey" | "queryFn"
  >,
) {
  // KAN-1964 — názvy metrik přicházejí v jazyce požadavku, takže jiný jazyk
  // je jiná odpověď. Prefix ``["public", "plans"]`` dál invaliduje admin.
  const { i18n } = useTranslation();
  return useQuery<PublicPlansListResponse>({
    queryKey: ["public", "plans", i18n.language],
    queryFn: () =>
      apiFetch<PublicPlansListResponse>(`${API}/public/plans`),
    // Short staleTime so a platform-admin edit reaches visitors
    // quickly. The admin save side also invalidates this query key
    // (see useAdminPlanUpdate) so operators see their own edits
    // immediately when both /admin/plans and /pricing-plans tabs
    // are open in the same browser.
    staleTime: 30 * 1000,
    ...options,
  });
}
